import { buildAccessState } from "./accessControl.js";

export const ACCESS_SESSION_CACHE_STORAGE_KEY = "grain-access-session-cache-v1";
export const ACCESS_SESSION_CACHE_TTL_MS = 30 * 60 * 1000;

function getDefaultWindow() {
  return typeof window !== "undefined" ? window : undefined;
}

function getSessionStorage(targetWindow = getDefaultWindow()) {
  try {
    return targetWindow?.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export function resolveAccessSessionUserId(session) {
  return typeof session?.user?.id === "string" ? session.user.id.trim() : "";
}

function normalizeOptionalBoolean(value) {
  return typeof value === "boolean" ? value : null;
}

function normalizeCacheInput(payload = null) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (
    "activeRole" in payload
    || "accountType" in payload
    || "dispatcherWorkspaceAccess" in payload
    || "driverPortalAccess" in payload
    || "userId" in payload
  ) {
    return {
      active_role: payload.activeRole,
      account_type: payload.accountType,
      dispatcher_workspace_access: payload.dispatcherWorkspaceAccess,
      driver_portal_access: payload.driverPortalAccess,
      permissions: payload.permissions,
      source: payload.source,
      user_id: payload.userId,
    };
  }

  return payload;
}

export function buildAccessSessionCachePayload(payload = null) {
  const access = buildAccessState(normalizeCacheInput(payload));
  const cachePayload = {
    active_role: access.activeRole,
    account_type: access.accountType,
    dispatcher_workspace_access: normalizeOptionalBoolean(access.dispatcherWorkspaceAccess),
    driver_portal_access: normalizeOptionalBoolean(access.driverPortalAccess),
    permissions: access.permissions,
    source: access.source,
    user_id: access.userId,
  };

  return cachePayload;
}

function hasCacheableAccessPayload(cachePayload) {
  if (!cachePayload?.user_id) {
    return false;
  }

  if (
    cachePayload.active_role === "admin"
    && (!Array.isArray(cachePayload.permissions) || cachePayload.permissions.length === 0)
  ) {
    return false;
  }

  return Boolean(
    cachePayload.active_role
    || cachePayload.account_type
    || cachePayload.source
    || cachePayload.dispatcher_workspace_access !== null
    || cachePayload.driver_portal_access !== null
    || (Array.isArray(cachePayload.permissions) && cachePayload.permissions.length > 0),
  );
}

function parseCachedEntry(rawEntry) {
  if (typeof rawEntry !== "string" || !rawEntry.trim()) {
    return null;
  }

  try {
    const parsedEntry = JSON.parse(rawEntry);
    if (!parsedEntry || typeof parsedEntry !== "object") {
      return null;
    }

    const savedAt = Number(parsedEntry.saved_at);
    if (!Number.isFinite(savedAt)) {
      return null;
    }

    const payload = buildAccessSessionCachePayload(parsedEntry.access);
    if (!hasCacheableAccessPayload(payload)) {
      return null;
    }

    return {
      payload,
      savedAt,
    };
  } catch {
    return null;
  }
}

export function clearAccessSessionCache({ targetWindow } = {}) {
  const storage = getSessionStorage(targetWindow);
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(ACCESS_SESSION_CACHE_STORAGE_KEY);
  } catch {}
}

export function writeAccessSessionCache(payload = null, {
  now = Date.now(),
  targetWindow,
} = {}) {
  const storage = getSessionStorage(targetWindow);
  if (!storage) {
    return null;
  }

  const cachePayload = buildAccessSessionCachePayload(payload);
  if (!hasCacheableAccessPayload(cachePayload)) {
    return null;
  }

  const entry = {
    access: cachePayload,
    saved_at: now,
    version: 1,
  };

  try {
    storage.setItem(ACCESS_SESSION_CACHE_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    return null;
  }

  return entry;
}

export function readAccessSessionCacheForSession(session, {
  now = Date.now(),
  targetWindow,
} = {}) {
  const storage = getSessionStorage(targetWindow);
  if (!storage) {
    return null;
  }

  const sessionUserId = resolveAccessSessionUserId(session);
  if (!sessionUserId) {
    return null;
  }

  let cachedEntry = null;
  try {
    cachedEntry = parseCachedEntry(storage.getItem(ACCESS_SESSION_CACHE_STORAGE_KEY));
  } catch {
    cachedEntry = null;
  }

  if (!cachedEntry) {
    clearAccessSessionCache({ targetWindow });
    return null;
  }

  if (cachedEntry.payload.user_id !== sessionUserId) {
    clearAccessSessionCache({ targetWindow });
    return null;
  }

  if (now - cachedEntry.savedAt > ACCESS_SESSION_CACHE_TTL_MS) {
    clearAccessSessionCache({ targetWindow });
    return null;
  }

  return {
    access: buildAccessState(cachedEntry.payload),
    payload: cachedEntry.payload,
    savedAt: cachedEntry.savedAt,
  };
}
